/* ────────────────────────────────────────────────────────────── *
 *  ExploreDialog — Full-width dialog for design exploration
 *
 *  Provides metric filtering and a DataGrid of generated samples.
 *  Row click selects a sample locally; Confirm commits it globally.
 *  State persists across open/close via useUIStore.exploreState.
 *
 *  CHANGES:
 *   - Confirm now routes through useSampleSelection.selectSampleWithGeometry
 *     instead of the raw store setter, so confirming a sample whose
 *     geometry hasn't been generated yet for the current compute job
 *     drives the same loading overlay/progress bar as the initial Compute.
 * ────────────────────────────────────────────────────────────── */

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DialogShell from '@/components/DialogShell';
import { useUIStore } from '@/stores/useUIStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { useInputStore } from '@/stores/useInputStore';
import type { SampleRow } from '@/types/sample';
import ExploreControls from './ExploreControls';
import ExploreTable from './ExploreTable';
import { getExploreMetrics, getFieldForMetric, getRadialMetricHeader } from './exploreColumns';
import { useExportExplore } from '@/hooks/useExportExplore';
import { useSampleSelection } from '@/hooks/useSampleSelection';

const ExploreDialog: React.FC = () => {
  const open = useUIStore((s) => s.exploreDialogOpen);
  const closeDialog = useUIStore((s) => s.closeDialog);
  const openDialog = useUIStore((s) => s.openDialog);
  const exploreState = useUIStore((s) => s.exploreState);
  const setExploreState = useUIStore((s) => s.setExploreState);
  const resetExploreState = useUIStore((s) => s.resetExploreState);

  const bushingType = useBushingStore((s) => s.bushingType);
  const generatedSamples = useSampleStore((s) => s.generatedSamples);
  const currentSelectedId = useSampleStore((s) => s.currentSelectedId);
  const bestSampleId = useSampleStore((s) => s.bestSampleId);

  const { selectSampleWithGeometry } = useSampleSelection();

  const [filteredRows, setFilteredRows] = useState<SampleRow[]>([]);
  const [message, setMessage] = useState<string>('');

  // Local selection state: only committed to global store on Confirm.
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(null);

  // Sync local selection from global store when dialog opens.
  useEffect(() => {
    if (open) {
      setLocalSelectedId(currentSelectedId);
    }
  }, [open, currentSelectedId]);

  // Reset explore state when bushing type changes
  useEffect(() => {
    if (open && exploreState.lastBushingType !== bushingType) {
      resetExploreState();
      setExploreState({ lastBushingType: bushingType });
    }
  }, [open, bushingType, exploreState.lastBushingType, resetExploreState, setExploreState]);

  // On open: initialise defaults if no metric is set, then apply filter.
  useEffect(() => {
    if (!open) return;

    let { metric, minVal, maxVal } = exploreState;

    if (!metric) {
      // Default to the primary radial (Cz) metric with ±15% of the target value.
      const radialHeader = getRadialMetricHeader(bushingType);
      const czTarget = useInputStore.getState().target.targets['Cz'] ?? null;
      const defaultMin = czTarget != null ? String(Math.round(czTarget * 0.85)) : '';
      const defaultMax = czTarget != null ? String(Math.round(czTarget * 1.15)) : '';

      metric = radialHeader;
      minVal = defaultMin;
      maxVal = defaultMax;
      setExploreState({ metric, minVal, maxVal });
    }

    applyFilter(metric, minVal, maxVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const metricOptions = useMemo(() => getExploreMetrics(bushingType), [bushingType]);

  const applyFilter = useCallback(
    (metric: string | null, minVal: string, maxVal: string) => {
      let rows = [...generatedSamples] as SampleRow[];

      if (metric) {
        const field = getFieldForMetric(bushingType, metric);
        if (field) {
          const lo = minVal !== '' ? parseFloat(minVal) : -Infinity;
          const hi = maxVal !== '' ? parseFloat(maxVal) : Infinity;

          if (!isNaN(lo) || !isNaN(hi)) {
            rows = rows.filter((row) => {
              const val = Number(row[field]);
              if (isNaN(val)) return false;
              return val >= (isNaN(lo) ? -Infinity : lo) && val <= (isNaN(hi) ? Infinity : hi);
            });
          }
        }
      }

      setFilteredRows(rows);
      setMessage(rows.length === 0 ? 'No rows match the filter criteria.' : '');
    },
    [generatedSamples, bushingType],
  );

  const handleFilter = useCallback(() => {
    applyFilter(exploreState.metric, exploreState.minVal, exploreState.maxVal);
  }, [applyFilter, exploreState]);

  const handleMetricChange = useCallback(
    (metric: string | null) => setExploreState({ metric }),
    [setExploreState],
  );

  const handleMinChange = useCallback(
    (val: string) => setExploreState({ minVal: val }),
    [setExploreState],
  );

  const handleMaxChange = useCallback(
    (val: string) => setExploreState({ maxVal: val }),
    [setExploreState],
  );

  const handleRowSelect = useCallback((id: string | null) => {
    if (id !== null) setLocalSelectedId(id);
  }, []);

  // Confirm commits the local selection to the global store, driving the
  // geometry loading overlay if this sample's /mesh hasn't run yet for
  // the current compute job.
  const handleConfirm = useCallback(() => {
    if (localSelectedId !== null) {
      void selectSampleWithGeometry(localSelectedId);
    }
    closeDialog('exploreDialog');
  }, [localSelectedId, selectSampleWithGeometry, closeDialog]);

  const handleClose = useCallback(() => {
    closeDialog('exploreDialog');
  }, [closeDialog]);

  const { exportToExcel } = useExportExplore();

  const handleExport = useCallback(() => {
    exportToExcel(filteredRows, bushingType, bestSampleId);
  }, [exportToExcel, filteredRows, bushingType, bestSampleId]);

  const dialogTitle = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Typography variant="inherit" color="primary.main" component="span">
        Explore Designs
      </Typography>
      <Tooltip title="Design Parameters Info">
        <IconButton
          size="small"
          onClick={() => openDialog('exploreInfoDialog')}
          sx={{
            bgcolor: '#1976d2',
            color: '#fff',
            '&:hover': { bgcolor: '#115293' },
          }}
          aria-label="Design Parameters Info"
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      maxWidth={1200}
      contentSx={{ minHeight: 520 }}
      actions={
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {filteredRows.length} design{filteredRows.length !== 1 ? 's' : ''} shown
          </Typography>
          <Button variant="contained" onClick={handleClose} color="info">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!localSelectedId}
            color="info"
            sx={{
              '&.Mui-disabled': {
                color: 'primary.main',
              },
            }}
          >
            Confirm
          </Button>
        </>
      }
    >
      <ExploreControls
        metricOptions={metricOptions}
        metric={exploreState.metric}
        minVal={exploreState.minVal}
        maxVal={exploreState.maxVal}
        onMetricChange={handleMetricChange}
        onMinChange={handleMinChange}
        onMaxChange={handleMaxChange}
        onFilter={handleFilter}
        onExport={handleExport}
        exportDisabled={filteredRows.length === 0}
      />

      <ExploreTable
        bushingType={bushingType}
        rows={filteredRows}
        selectedId={localSelectedId}
        bestSampleId={bestSampleId}
        onRowSelect={handleRowSelect}
        message={message}
      />
    </DialogShell>
  );
};

export default ExploreDialog;